import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Bot, User as UserIcon, Sparkles, RefreshCw, BookOpen, Plus, Trash2, MessageCircle, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import SectionHeader from "../components/SectionHeader";
import { SERVICES, SITE } from "../data/site";

// ── WhatsApp live agent number ─────────────────────────────────────────────
const WA_NUMBER = "2348113748129";
const WA_URL = `https://wa.me/${WA_NUMBER}`;

// ── Built-in knowledge library (seeded from project content) ──────────────
const SEED_LIBRARY = [
  {
    id: "about",
    title: "About Amcblast Solutions",
    content: `Amcblast Solutions Limited (RC: 943327) is an indigenous dynamic software solutions company founded in 2009 and incorporated in 2011. We are committed to building the best and most simplified Web, Mobile and Social applications at an affordable cost. Our tagline is "We set the pace."

We help clients run their businesses easily and effectively with scalable architectures, cloud deployments, SEO/SEM and application maintenance. We have proven technology experts who can deliver results exactly as imagined.

Key stats: 17+ years in business, 12 specialists, 240+ projects delivered, 16 service lines.`
  },
  {
    id: "offices",
    title: "Office Locations & Contact",
    content: `Amcblast Solutions Limited has three offices:

1. Head Office: Old Ojo Road, Maza Maza, Lagos, Nigeria
2. Home of Automation: Ikota Shopping Complex, KM 22 Lagos-Epe Expressway
3. United Kingdom: Cardiff, United Kingdom

Phone: +234 803 954 7692
WhatsApp: +234 811 374 8129
Email: info@amcblastsolutions.com
Website: www.amcblastsolutions.com

Office hours: 8am–7pm WAT, Monday to Saturday.`
  },
  {
    id: "services",
    title: "Services Offered",
    content: `Amcblast Solutions offers 16 specialist service lines:

1. Web Design – corporate websites, portals, landing pages, CMS integration
2. Software Development – custom software, agile sprints, cloud-native deployment
3. Mobile App Creation – iOS and Android (React Native, Flutter), app store submission
4. Technology Consultancy – digital strategy, solution architecture, roadmaps
5. Corporate Training – cloud, DevOps, security curricula, hands-on labs
6. BulkSMS Solution – sender-ID provisioning, API & dashboard, OTP, delivery reports
7. Payment Gateway Integration – Paystack, Flutterwave, Remita, Stripe, PayPal
8. Site Template Sale – ready-made templates, hosting, SSL set-up
9. Company Registration with CAC – name reservation, incorporation, TIN registration
10. Home Automation & CCTV – Hikvision/Dahua cameras, smart lighting, access control
11. OWASP Software Security Testing – penetration testing, code review, ASVS assessment
12. IT Infrastructure Procurement – Dell, HP, APC, Cisco, Fortinet supply
13. Structured Cabling & Networks – CAT5E to CAT7, fibre optic, certified testing
14. Data Center & Server Deployment – HVAC, raised floor, server rack integration
15. IT Power Solutions – APC Schneider UPS, battery maintenance, SLAs
16. Project Management & Consulting – PMI/ITIL-aligned PM, stakeholder management`
  },
  {
    id: "partners",
    title: "Technology Partners & Accreditations",
    content: `Amcblast Solutions is accredited and partners with:

Hardware & Infrastructure: Microsoft Certified Partner, Dell Technologies Gold, APC Schneider Premier Partner, IBM Business Partner, Fortinet Bronze Partner, Kaspersky Registered Partner, Honeywell, Hikvision, Cisco, Aruba

AI & Cloud Platforms: Anthropic Claude, OpenAI API, AWS Bedrock, Microsoft Azure, CoreWeave, Scale AI

Developer Tools: GitHub, Kubernetes, Figma, Notion, Linear, SonarQube, Datadog, Mixpanel

Security: CrowdStrike, Cloudflare, Wiz, CymorthAI`
  },
  {
    id: "clients",
    title: "Clients & Sectors Served",
    content: `Amcblast Solutions has served clients across multiple sectors:

Healthcare: Paramount Medical & Pharmaceutical Services, NGPMPS Pharmaceutical Services
Education: Nigerian Army Qualification Examination, Johngbor Foundation, Funbis International School, Lagos State Polytechnic Online Library, Lagos State University, IMS Computer School, Star Systems Computer School, Giwat Web Creation & Training Centre, Molad International School
Finance: Oparamou Microfinance, GTBank Alert Integration, FBN Alert Integration
Real Estate: Young Shall Grow Properties, Brickwall Global, A.O. Williams Planning & Consultants (UK & Nigeria), De Prestige Housing, Fin ICT Hubs Limited
Hospitality: Rockview Hotels Ltd, Prestige Lounge
General Business: Apacino Limited, Return Over Investment Co. Ltd (Ghana), YSG Uni-delivery Limited`
  },
  {
    id: "pricing",
    title: "Pricing & Packages",
    content: `Amcblast Solutions offers three pricing tiers:

ESSENTIALS – ₦450,000 / project
For startups and SMEs needing a focused, fast launch.
- Brochure-grade website or single workflow build
- Up to 6 pages or 1 core module
- 1 round of revisions
- Email & WhatsApp support

GROWTH – ₦2,500,000+ / engagement
The sweet spot for mid-sized businesses transforming operations.
- Full pod (PM + 2 devs + designer + QA)
- Custom software or full IT infrastructure rollout
- Cloud / on-prem deployment & training
- Priority support, 5×8 SLA

ENTERPRISE – Custom pricing
Mission-critical platforms with dedicated SLAs.
- Multi-pod delivery and on-site teams
- Architecture, security and compliance reviews
- 24×7 monitoring and on-call
- Quarterly business reviews

Contact us for a detailed quote: info@amcblastsolutions.com or WhatsApp +234 811 374 8129`
  },
  {
    id: "process",
    title: "Project Process & Delivery",
    content: `Amcblast Solutions follows a 6-step delivery roadmap:

Step 1 – Discover & Assess: Stakeholder interviews, site survey, and current-state audit.
Step 2 – Design & Architect: Solution design, bill of materials (BOM), and clear delivery plan.
Step 3 – Build & Configure: Develop, integrate and configure to industry standards.
Step 4 – Test & Certify: QA, OWASP-aligned security testing and certified line testing.
Step 5 – Deploy & Train: Cut-over, knowledge transfer and user enablement.
Step 6 – Operate & Improve: SLAs, monitoring and continuous improvement.

Typical engagement timeline: 8–16 weeks. We share a detailed roadmap after a 1-hour discovery call.
We sign mutual NDAs before any commercially sensitive discussion.
Every project includes a hyper-care period and optional SLAs for ongoing managed support.`
  },
  {
    id: "values",
    title: "Company Values & Philosophy",
    content: `Amcblast Solutions core values:

Integrity: Honesty and transparency in every brief, proposal and invoice.
Excellence: Consistency and quality through standardisation and senior craft.
Partnership: We win when you win — a long-term, relationship-first posture.
Performance: A passion for winning and the discipline to deliver outcomes.

Philosophy: Build a complete product for any given industry through extensive research, expert involvement, and consistency through standardization.

Mission: To provide companies with a personal and pro-active approach to maintaining and improving their IT infrastructure and software requirements.

Vision: Our vision serves as the framework for our roadmap — describing what we need to do to continue to be an innovative leader setting the pace.`
  },
  {
    id: "technologies",
    title: "Technologies & Specialisations",
    content: `Amcblast Solutions specialises in:

Web: React, Next.js, WordPress, HTML/Bootstrap 5
Backend/Software: Node.js, Python, FastAPI, .NET
Mobile: React Native, Flutter, iOS & Android native
Cloud: AWS, GCP, Microsoft Azure, AWS Bedrock
Networks: Cisco, Aruba, Fortinet, MPLS, VPN
Security: OWASP Top 10, ASVS, penetration testing, Fortinet, CrowdStrike
Power: APC Schneider UPS, Sollatek
Hardware: Dell, HP, IBM, Hikvision, Dahua
AI: Anthropic Claude, OpenAI API, LLM integrations
DevOps: Kubernetes, GitHub, CI/CD pipelines, Docker
Data & Analytics: Mixpanel, Datadog, SonarQube`
  },
];

// ── Storage helpers ────────────────────────────────────────────────────────
const STORAGE_KEY = "amcbot_knowledge_library";

function loadLibrary() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Merge with seed, seed entries take precedence by id
      const seedIds = new Set(SEED_LIBRARY.map(s => s.id));
      const userEntries = parsed.filter(e => !seedIds.has(e.id));
      return [...SEED_LIBRARY, ...userEntries];
    }
  } catch {}
  return [...SEED_LIBRARY];
}

function saveUserEntries(allEntries) {
  const seedIds = new Set(SEED_LIBRARY.map(s => s.id));
  const userEntries = allEntries.filter(e => !seedIds.has(e.id));
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(userEntries));
  } catch {}
}

// ── Knowledge search ────────────────────────────────────────────────────────
function searchLibrary(query, library) {
  const q = query.toLowerCase();
  const scored = library.map(entry => {
    const titleScore = entry.title.toLowerCase().includes(q) ? 3 : 0;
    const words = q.split(/\s+/).filter(w => w.length > 2);
    const contentScore = words.reduce((acc, w) => {
      const count = (entry.content.toLowerCase().match(new RegExp(w, "g")) || []).length;
      return acc + count;
    }, 0);
    return { entry, score: titleScore + contentScore };
  });
  return scored.filter(s => s.score > 0).sort((a, b) => b.score - a.score);
}

// ── Anthropic API call ──────────────────────────────────────────────────────
async function askClaude(userMessage, context) {
  try {
    const response = await fetch("https://api.anthropic.com/v1/messages", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        model: "claude-sonnet-4-20250514",
        max_tokens: 1000,
        system: `You are AmcBot, the professional virtual assistant for Amcblast Solutions Limited, a Nigerian IT company. 
You must ONLY answer based on the provided knowledge context. Be professional, concise and helpful.
Always refer to the company as "Amcblast Solutions" or "we/our team". 
If asked about pricing, always suggest contacting via WhatsApp (+234 811 374 8129) or email for a detailed quote.
Format your response clearly. Do not make up information not in the context.

KNOWLEDGE CONTEXT:
${context}`,
        messages: [{ role: "user", content: userMessage }],
      }),
    });
    if (!response.ok) throw new Error("API error");
    const data = await response.json();
    return data.content?.[0]?.text || null;
  } catch {
    return null;
  }
}

// ── Session ID ─────────────────────────────────────────────────────────────
function genSessionId() {
  return "amc-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 8);
}

// ── WhatsApp Live Agent Message ────────────────────────────────────────────
const LIVE_AGENT_MSG = {
  role: "assistant",
  content: "LIVE_AGENT",
};

// ── STARTER PROMPTS ────────────────────────────────────────────────────────
const STARTER_PROMPTS = [
  "What services do you offer?",
  "How do I get a job quote?",
  "Where are your offices?",
  "Do you build mobile apps?",
  "Tell me about your security testing",
  "How long does a project take?",
  "What are your prices?",
  "Who are your technology partners?",
];

// ═══════════════════════════════════════════════════════════════════════════
export default function FAQ() {
  const [library, setLibrary] = useState(loadLibrary);
  const [activeTab, setActiveTab] = useState("chat"); // "chat" | "library"
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Hi 👋 I'm AmcBot — Amcblast Solutions' virtual assistant. I have a full knowledge library about our services, pricing, team and process. How can I help you today?",
    },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  // Library editor state
  const [newTitle, setNewTitle] = useState("");
  const [newContent, setNewContent] = useState("");
  const [libMsg, setLibMsg] = useState("");

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const resetChat = () => {
    setMessages([{
      role: "assistant",
      content: "Fresh start! 👋 I'm AmcBot. How can I help you today?",
    }]);
  };

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || sending) return;
    setInput("");
    setMessages(m => [...m, { role: "user", content }]);
    setSending(true);

    // Search knowledge library
    const results = searchLibrary(content, library);

    if (results.length > 0) {
      // Build context from top matches
      const context = results
        .slice(0, 3)
        .map(r => `## ${r.entry.title}\n${r.entry.content}`)
        .join("\n\n---\n\n");

      // Try Claude API first for polished response
      const aiReply = await askClaude(content, context);

      if (aiReply) {
        setMessages(m => [...m, { role: "assistant", content: aiReply }]);
      } else {
        // Fallback: use top match content directly
        const top = results[0].entry;
        setMessages(m => [...m, {
          role: "assistant",
          content: `Based on our knowledge base:\n\n**${top.title}**\n\n${top.content.slice(0, 600)}${top.content.length > 600 ? "...\n\nFor more details, feel free to reach out to our team directly." : ""}`,
        }]);
      }
    } else {
      // Not in library → transfer to live agent
      setMessages(m => [...m, {
        role: "assistant",
        content: "TRANSFER_TO_AGENT",
        transferMsg: content,
      }]);
    }

    setSending(false);
  };

  const addLibraryEntry = () => {
    if (!newTitle.trim() || !newContent.trim()) {
      setLibMsg("Please fill in both title and content.");
      return;
    }
    const entry = {
      id: "user-" + Date.now(),
      title: newTitle.trim(),
      content: newContent.trim(),
    };
    const updated = [...library, entry];
    setLibrary(updated);
    saveUserEntries(updated);
    setNewTitle("");
    setNewContent("");
    setLibMsg(`✅ "${entry.title}" added to library.`);
    setTimeout(() => setLibMsg(""), 3000);
  };

  const deleteEntry = (id) => {
    const seedIds = new Set(SEED_LIBRARY.map(s => s.id));
    if (seedIds.has(id)) {
      setLibMsg("❌ Built-in entries cannot be deleted.");
      setTimeout(() => setLibMsg(""), 3000);
      return;
    }
    const updated = library.filter(e => e.id !== id);
    setLibrary(updated);
    saveUserEntries(updated);
    setLibMsg("Entry removed.");
    setTimeout(() => setLibMsg(""), 2000);
  };

  return (
    <section className="container-st pt-16 pb-24">
      <SectionHeader
        eyebrow="Help Centre"
        title="How can we assist you?"
        subtitle="Chat with AmcBot — powered by our knowledge library and AI. Ask anything about Amcblast Solutions services, pricing, process and more."
        center
      />

      {/* Tab switcher */}
      <div className="mt-8 flex justify-center gap-2">
        <button
          onClick={() => setActiveTab("chat")}
          className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition ${
            activeTab === "chat"
              ? "bg-blue-600 text-white"
              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
          }`}
        >
          <Bot className="w-4 h-4" /> Chat with AmcBot
        </button>
        <button
          onClick={() => setActiveTab("library")}
          className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition ${
            activeTab === "library"
              ? "bg-blue-600 text-white"
              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
          }`}
        >
          <BookOpen className="w-4 h-4" /> Knowledge Library ({library.length})
        </button>
      </div>

      <div className="mt-8 max-w-6xl mx-auto">
        {/* ── CHAT TAB ─────────────────────────────────────────────────── */}
        {activeTab === "chat" && (
          <div className="grid lg:grid-cols-12 gap-6">
            {/* Chat panel */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="lg:col-span-8 rounded-3xl border border-slate-100 bg-white shadow-sm overflow-hidden flex flex-col h-[640px]"
              data-testid="chatbot-panel"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-white/15 backdrop-blur grid place-items-center">
                    <Bot className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-semibold text-sm">AmcBot</div>
                    <div className="text-[11px] text-blue-100 flex items-center gap-1.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-300 animate-pulse" />
                      AI-powered · Knowledge library + Claude AI
                    </div>
                  </div>
                </div>
                <button
                  onClick={resetChat}
                  data-testid="chat-reset"
                  className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 transition"
                >
                  <RefreshCw className="w-3 h-3" /> New chat
                </button>
              </div>

              {/* Messages */}
              <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-6 space-y-4 bg-slate-50/40" data-testid="chat-messages">
                <AnimatePresence initial={false}>
                  {messages.map((m, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className={`flex gap-3 ${m.role === "user" ? "justify-end" : "justify-start"}`}
                      data-testid={`chat-msg-${m.role}-${i}`}
                    >
                      {m.role === "assistant" && (
                        <div className="h-8 w-8 rounded-full bg-blue-600 grid place-items-center text-white shrink-0">
                          <Bot className="w-4 h-4" />
                        </div>
                      )}

                      {/* Live agent transfer card */}
                      {m.role === "assistant" && m.content === "TRANSFER_TO_AGENT" ? (
                        <div className="max-w-[78%] rounded-2xl rounded-bl-md overflow-hidden border border-slate-100 bg-white shadow-sm">
                          <div className="bg-amber-50 border-b border-amber-100 px-4 py-3">
                            <p className="text-sm text-amber-800 font-semibold">⏳ Please wait — transferring you to a live agent...</p>
                          </div>
                          <div className="px-4 py-4">
                            <p className="text-sm text-slate-600 mb-3">
                              I couldn't find information about that in my knowledge base. A member of our team will be happy to help you directly.
                            </p>
                            <a
                              href={`${WA_URL}?text=Hello Amcblast, I need help with: ${encodeURIComponent(m.transferMsg || "")}`}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white text-sm font-semibold px-5 py-2.5 rounded-full transition"
                            >
                              <MessageCircle className="w-4 h-4" />
                              Click to speak with Live Support
                              <ExternalLink className="w-3 h-3" />
                            </a>
                            <p className="text-xs text-slate-400 mt-2">Opens WhatsApp · +234 811 374 8129</p>
                          </div>
                        </div>
                      ) : m.role === "assistant" ? (
                        <div className="max-w-[78%] rounded-2xl px-4 py-3 text-sm leading-relaxed bg-white border border-slate-100 text-slate-800 rounded-bl-md whitespace-pre-wrap">
                          {m.content}
                        </div>
                      ) : (
                        <div className="max-w-[78%] rounded-2xl px-4 py-3 text-sm leading-relaxed bg-blue-600 text-white rounded-br-md whitespace-pre-wrap">
                          {m.content}
                        </div>
                      )}

                      {m.role === "user" && (
                        <div className="h-8 w-8 rounded-full bg-slate-900 grid place-items-center text-white shrink-0">
                          <UserIcon className="w-4 h-4" />
                        </div>
                      )}
                    </motion.div>
                  ))}
                </AnimatePresence>

                {sending && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex gap-3 justify-start"
                    data-testid="chat-typing"
                  >
                    <div className="h-8 w-8 rounded-full bg-blue-600 grid place-items-center text-white">
                      <Bot className="w-4 h-4" />
                    </div>
                    <div className="bg-white border border-slate-100 rounded-2xl px-4 py-3 flex gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                      <span className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                      <span className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "300ms" }} />
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Composer */}
              <form
                onSubmit={(e) => { e.preventDefault(); send(); }}
                className="px-4 py-3 border-t border-slate-100 bg-white flex items-end gap-2"
                data-testid="chat-form"
              >
                <textarea
                  data-testid="chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  rows={1}
                  placeholder="Ask anything about our services, pricing, projects..."
                  className="flex-1 resize-none max-h-32 px-4 py-3 rounded-2xl bg-slate-50 border border-slate-200 focus:bg-white focus:border-blue-400 focus:outline-none text-sm"
                />
                <button
                  type="submit"
                  disabled={sending || !input.trim()}
                  data-testid="chat-send"
                  className="h-11 w-11 rounded-2xl bg-blue-600 hover:bg-blue-700 text-white grid place-items-center disabled:opacity-50 transition"
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </motion.div>

            {/* Side panel */}
            <div className="lg:col-span-4 space-y-4">
              <div className="rounded-3xl border border-slate-100 bg-white p-6">
                <div className="inline-flex items-center gap-2 text-xs font-bold tracking-widest uppercase text-blue-600">
                  <Sparkles className="w-3.5 h-3.5" /> Quick questions
                </div>
                <div className="mt-4 flex flex-col gap-2">
                  {STARTER_PROMPTS.map((q, i) => (
                    <button
                      key={q}
                      onClick={() => send(q)}
                      data-testid={`starter-prompt-${i}`}
                      className="text-left text-sm px-4 py-2.5 rounded-2xl border border-slate-100 hover:border-blue-200 hover:bg-blue-50/40 text-slate-700 transition"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>

              <div className="rounded-3xl bg-slate-950 text-white p-6">
                <div className="text-xs uppercase tracking-widest text-blue-400 font-semibold">
                  Prefer to talk to a human?
                </div>
                <p className="mt-2 text-sm text-slate-300">Our team is online 8am–7pm WAT.</p>
                <a
                  href={`tel:${SITE.phone}`}
                  className="mt-4 block text-sm font-semibold hover:text-blue-400"
                >
                  📞 {SITE.phone}
                </a>
                <a
                  href={WA_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2 block text-sm font-semibold text-green-400 hover:text-green-300"
                >
                  💬 WhatsApp Live Support
                </a>
                <a
                  href={`mailto:${SITE.email}`}
                  className="mt-1 block text-sm font-semibold hover:text-blue-400 break-all"
                >
                  ✉ {SITE.email}
                </a>
                <Link
                  to="/contact?type=quote"
                  data-testid="chat-quote-cta"
                  className="mt-5 block text-center bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-5 py-3 rounded-full"
                >
                  Get a Job Quote
                </Link>
              </div>

              <div className="rounded-3xl border border-slate-100 bg-white p-6">
                <div className="text-xs uppercase tracking-widest text-blue-600 font-semibold">Popular services</div>
                <ul className="mt-3 space-y-2 text-sm">
                  {SERVICES.slice(0, 6).map((s) => (
                    <li key={s.slug}>
                      <Link to={`/services/${s.slug}`} className="text-slate-700 hover:text-blue-700">
                        → {s.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* ── LIBRARY TAB ──────────────────────────────────────────────── */}
        {activeTab === "library" && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Add new entry */}
            <div className="rounded-3xl border border-blue-100 bg-blue-50/30 p-6">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <Plus className="w-5 h-5 text-blue-600" /> Add Knowledge Entry
              </h3>
              <p className="text-sm text-slate-500 mt-1">Add writeups, product info, or any content you want AmcBot to reference when answering questions.</p>
              <div className="mt-4 space-y-3">
                <input
                  type="text"
                  value={newTitle}
                  onChange={e => setNewTitle(e.target.value)}
                  placeholder="Title (e.g. 'Our New AI Product', 'Pricing Update')"
                  className="w-full px-4 py-3 rounded-2xl border border-slate-200 bg-white text-sm focus:outline-none focus:border-blue-400"
                />
                <textarea
                  value={newContent}
                  onChange={e => setNewContent(e.target.value)}
                  placeholder="Paste your content here — service descriptions, FAQs, policies, product details..."
                  rows={6}
                  className="w-full px-4 py-3 rounded-2xl border border-slate-200 bg-white text-sm focus:outline-none focus:border-blue-400 resize-none"
                />
                <div className="flex items-center gap-3">
                  <button
                    onClick={addLibraryEntry}
                    className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-6 py-2.5 rounded-full transition"
                  >
                    <Plus className="w-4 h-4" /> Add to Library
                  </button>
                  {libMsg && <p className="text-sm text-green-600 font-medium">{libMsg}</p>}
                </div>
              </div>
            </div>

            {/* Library entries */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {library.map((entry) => {
                const isBuiltIn = SEED_LIBRARY.some(s => s.id === entry.id);
                return (
                  <div key={entry.id} className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm relative">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <span className={`text-[10px] font-bold uppercase tracking-widest ${isBuiltIn ? "text-blue-500" : "text-green-600"}`}>
                          {isBuiltIn ? "Built-in" : "Custom"}
                        </span>
                        <h4 className="font-semibold text-slate-900 text-sm mt-0.5">{entry.title}</h4>
                      </div>
                      {!isBuiltIn && (
                        <button
                          onClick={() => deleteEntry(entry.id)}
                          className="text-slate-300 hover:text-red-400 transition shrink-0"
                          title="Delete entry"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <p className="mt-2 text-xs text-slate-500 leading-relaxed line-clamp-4">
                      {entry.content.slice(0, 180)}...
                    </p>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
}
